export interface NumberFormatOptions {
    useGroupFormat?: boolean;
    alwaysUseSign?: boolean;
}

class NumberFormatters{
    private readonly thousandSeparator: string = '\u202F'; // Thin unbreakable space
    private readonly decimalSeparator: string = '.';
    
    private readonly numberFormatter0 = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    private readonly numberFormatter1 = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });
    
    private readonly numberFormatter2 = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    private readonly numberFormatter3 = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 3,
        maximumFractionDigits: 3,
    });

    private readonly numberFormatter4 = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
    });
    
    private readonly numberFormatter6 = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
    });

    readonly defaultNumberFormatOptions: Required<NumberFormatOptions> = {
        useGroupFormat: true,
        alwaysUseSign: false
    };

    readonly hideGroupDelimiterFormatOptions: NumberFormatOptions = {
        useGroupFormat: false,
        alwaysUseSign: false
    };
    
    format0 = (value: number | null | undefined, options: NumberFormatOptions = this.defaultNumberFormatOptions): string =>
        this.format(value, this.numberFormatter0, options);

    format1 = (value: number | null | undefined, options: NumberFormatOptions = this.defaultNumberFormatOptions): string =>
        this.format(value, this.numberFormatter1, options);
    
    format2 = (value: number | null | undefined, options: NumberFormatOptions = this.defaultNumberFormatOptions): string =>
        this.format(value, this.numberFormatter2, options);
    
    format3 = (value: number | null | undefined, options: NumberFormatOptions = this.defaultNumberFormatOptions): string =>
        this.format(value, this.numberFormatter3, options);

    format4 = (value: number | null | undefined, options: NumberFormatOptions = this.defaultNumberFormatOptions): string =>
        this.format(value, this.numberFormatter4, options);

    format6 = (value: number | null | undefined, options: NumberFormatOptions = this.defaultNumberFormatOptions): string =>
        this.format(value, this.numberFormatter6, options);
    
    private format(
        value: number | null | undefined,
        numberFormatter: Intl.NumberFormat,
        options: NumberFormatOptions,
    ) {
        if (value === null || value === undefined){
            return '';
        }

        const mergedOptions = { ...this.defaultNumberFormatOptions, ...options };
        
        const parts = numberFormatter.formatToParts(value);
        const formattedValue = parts
            .map(part => {
                if (part.type === 'group') return mergedOptions.useGroupFormat ? this.thousandSeparator : '';
                if (part.type === 'decimal') return this.decimalSeparator;
                return part.value;
            })
            .join('');
        
        return mergedOptions.alwaysUseSign
            ? value > 0
                ? `+${formattedValue}`
                : formattedValue
            : formattedValue;
    }
}

export const numberFormatters = new NumberFormatters();